import React from 'react';
import { Box, Popover, IconButton, Avatar, Tooltip, Typography } from '@mui/material';
import UserActions from './user-actions';
import ManageCompanyMenu from './manage-company-menu';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '../providers/user-provider';
import Cookies from 'js-cookie';
interface UserMenuProps {
    darkMode?: boolean;
    onDarkModeChange?: (checked: boolean) => void;
    onAccount?: () => void;
    onHelp?: () => void;
    onLogout?: () => void;
}

const UserMenu: React.FC<UserMenuProps> = ({
    darkMode = false,
    onDarkModeChange = () => {},
    onAccount = () => {},
    onHelp = () => {},
    onLogout = () => {},
}) => {
    const { user } = useUser();
    const location = useLocation();
    const navigate = useNavigate();
    const isAdmin = location.pathname.startsWith('/admin');
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [companyMenuAnchorEl, setCompanyMenuAnchorEl] = React.useState<null | HTMLElement>(null);
    const companyButtonRef = React.useRef<HTMLElement | null>(null);
    const open = Boolean(anchorEl);
    const companyMenuOpen = Boolean(companyMenuAnchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
        setCompanyMenuAnchorEl(null);
    };

    const getDisplayName = (): string => {
        const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
        return name || user?.email || 'Unknown User';
    };

    const getCurrentCompanyName = (): string => {
        if (!user?.entityId) return 'Company Name';
        try {
            const companiesCookie = Cookies.get('entities');
            const companies = companiesCookie ? JSON.parse(companiesCookie) : [];
            const currentCompany = companies.find((c: any) => c.entityId === user.entityId);
            return currentCompany?.entityName || 'Company Name';
        } catch {
            return 'Company Name';
        }
    };

    const getCurrentUserRole = (): string => {
        if (!user?.entityId) return 'User';
        try {
            const companiesCookie = Cookies.get('entities');
            const companies = companiesCookie ? JSON.parse(companiesCookie) : [];
            const currentCompany = companies.find((c: any) => c.entityId === user.entityId);
            return currentCompany?.role === 'admin' ? 'Admin' : 'Read Only';
        } catch {
            return 'User';
        }
    };

    const displayName = getDisplayName();
    // First + last initial (or the first letter of the email) - shown in the
    // avatar circle that opens this menu, the way most apps do it.
    const initials = (
        [user?.firstName, user?.lastName].filter(Boolean).map(n => String(n).trim()[0]).join('')
        || (user?.email || '?')[0]
    ).toUpperCase();
    // Swapped from the original layout (company name on top, role + email
    // below): the user's own name/email now leads, and the company name -
    // which still needs to be visible somewhere - moved down alongside the
    // role.
    const subtitle = `${getCurrentUserRole()} • ${getCurrentCompanyName()}`;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {/* Initials avatar with the current company underneath - the name
                and role show on hover and in the menu it opens. */}
            <Tooltip title={`${displayName} · ${subtitle}`} arrow>
                <IconButton
                    onClick={handleClick}
                    aria-label="Account menu"
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    sx={{
                        p: 0.25,
                        border: '2px solid',
                        borderColor: open ? 'var(--color-primary)' : 'transparent',
                        transition: 'border-color 0.15s ease',
                        '&:hover': { borderColor: 'rgba(var(--color-primary-rgb), 0.5)' },
                    }}
                >
                    <Avatar
                        src={user?.avatarUrl || undefined}
                        alt={displayName}
                        sx={{ width: 34, height: 34, fontSize: 14, fontWeight: 700, bgcolor: 'var(--color-primary)', color: '#fff' }}
                    >
                        {initials}
                    </Avatar>
                </IconButton>
            </Tooltip>
            <Typography
                title={getCurrentCompanyName()}
                sx={{
                    fontSize: 10,
                    lineHeight: 1.2,
                    mt: 0.25,
                    maxWidth: 110,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: isAdmin ? '#fff' : 'var(--color-default-text)',
                    opacity: 0.75,
                }}
            >
                {getCurrentCompanyName()}
            </Typography>
            <Popover
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                    sx: {
                        mt: 1,
                        minWidth: 280,
                        boxShadow: '0 2px 12px #e5e7eb',
                        borderRadius: 2,
                        p: 0,
                    },
                }}
            >
                <UserActions
                    user={{ name: getDisplayName(), email: user?.email || '', avatarUrl: user?.avatarUrl }}
                    onAccount={() => { isAdmin ? navigate('/admin/profile') : onAccount(); handleClose(); }}
                    onHelp={() => { onHelp(); handleClose(); }}
                    onLogout={() => { onLogout(); handleClose(); }}
                    onBillingSubscription={() => { navigate('/settings/company#subscription'); handleClose(); }}
                    onCreateCompany={() => {
                        if (companyButtonRef.current) {
                            setCompanyMenuAnchorEl(companyButtonRef.current);
                        }
                    }}
                    isAdmin={isAdmin}
                    companyButtonRef={companyButtonRef}
                />
            </Popover>
            <Popover
                anchorEl={companyMenuAnchorEl}
                open={companyMenuOpen}
                onClose={() => setCompanyMenuAnchorEl(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                    sx: {
                        boxShadow: '0 2px 12px #e5e7eb',
                        borderRadius: 2,
                        p: 0,
                    },
                }}
            >
                <ManageCompanyMenu
                    onBack={() => setCompanyMenuAnchorEl(null)}
                    onCreateCompany={() => {
                        navigate('/transaction?createCompany=true', { replace: true });
                        handleClose();
                    }}
                />
            </Popover>
        </Box>
    );
};

export default UserMenu;

